// P-OBSERVABILITY-A — Sentry wiring (closes #4 in Alex's tracker).
//
// WHY THIS EXISTS
// ~6h were burned on 2026-05-30/31 chasing two *silent* prod failures:
//   • BUG 2  — child_attempts INSERT returned 42501 (missing GRANT) for
//              every authenticated user since Phase G4. attemptLogger
//              console.error'd it and dropped the attempt. No surface.
//   • OAuth  — Mac OAuth callback silently dropped the URL fragment
//              (implicit flow), so sign-in failed with no error trail.
// Both would have shown up in Sentry within minutes. This module is the
// thin foundation: init in prod only, plus an explicit capture helper
// for the known swallow-the-error paths.
//
// SCOPE (deliberately minimal — see #4 for the deferred list):
//   • prod-only init (no dev noise / no event spend in local builds)
//   • basic performance tracing @ 10% (default browser integration)
//   • error-only session replay @ 10% (NO full-session capture)
//   • NO user identification / PII (privacy — this is a kids' app)
//
// CONFIG SOURCES
//   VITE_SENTRY_DSN              — Vercel env var (NOT committed). Absent
//                                  in dev/preview → init is skipped.
//   VITE_VERCEL_GIT_COMMIT_SHA  — Vercel-exposed commit SHA → release tag
//                                  (falls back to "unknown" when unset).

import * as Sentry from "@sentry/react";

// Re-export so callers can reach <Sentry.ErrorBoundary> etc. through one
// module rather than importing @sentry/react in three places.
export { Sentry };

// Tracks whether init actually ran so captureSupabaseError can no-op its
// Sentry call in dev (where init is intentionally skipped) while still
// logging to the console.
let initialized = false;

/** Initialize Sentry — call once, at the very top of the entry module,
 * before any app code runs. No-op (returns false) when there's no DSN or
 * we're in a Vite dev build, so local dev stays quiet and no events are
 * sent until a real DSN is configured in Vercel. */
// MA-10 — the PII scrub (scrubEvent / scrubBreadcrumb) lives in the leaf
// ./sentryScrub.ts (MA-1044 moved it verbatim, so the readiness check's own
// errors-only Sentry can use it without loading this module's replay setup).
// Re-exported here: every importer is unchanged.
import { scrubEvent, scrubBreadcrumb } from "./sentryScrub";
export { scrubEvent, scrubBreadcrumb };

export function initSentry(): boolean {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Skip entirely in dev or when unconfigured. import.meta.env.DEV is
  // true for `vite`/`vite dev`; false for `vite build` output.
  if (!dsn || import.meta.env.DEV) return false;

  Sentry.init({
    dsn,
    // MA-10 — scrub PII from every outgoing event/breadcrumb.
    beforeSend(event) {
      return scrubEvent(event);
    },
    beforeBreadcrumb(breadcrumb) {
      return scrubBreadcrumb(breadcrumb);
    },
    // MODE is "production" for a normal `vite build`; the fallback keeps
    // the tag sane if a custom mode is ever used.
    environment: import.meta.env.MODE || "production",
    release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA || "unknown",
    integrations: [
      // Automatic pageload/navigation spans only — no custom spans yet.
      Sentry.browserTracingIntegration(),
      // Replay is privacy-hardened: mask all text + block all media so no
      // child-facing content or PII is ever recorded.
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    // Calibration targets (start conservative, tune later — see #4):
    tracesSampleRate: 0.1, // 10% of transactions get performance traces
    replaysSessionSampleRate: 0, // never record full sessions
    replaysOnErrorSampleRate: 0.1, // record replay for 10% of errored sessions
  });
  initialized = true;
  return true;
}

/** Report a React render crash. Called from the top-level ErrorBoundary's
 * componentDidCatch (which keeps owning the user-facing fallback + the
 * sessionLog repro trail). No-op when init didn't run (dev / no DSN).
 * Safe to call unconditionally. */
export function captureRenderError(error: unknown, componentStack?: string): void {
  if (!initialized) return;
  Sentry.captureException(error, {
    tags: { area: "react-render" },
    extra: { componentStack: componentStack ?? "" },
  });
}

/** Report an error from a known silent-failure path (a supabase write or
 * read that gets caught and swallowed). Always console.error's so dev —
 * where Sentry is off — still sees it; sends to Sentry only when init ran.
 * Safe to call unconditionally. */
export function captureSupabaseError(
  label: string,
  error: unknown,
  context?: Record<string, unknown>,
): void {
  // Console first so the failure is visible in dev (Sentry skipped there)
  // and in prod browser consoles regardless of Sentry state.
  console.error(`[supabase] ${label}:`, error, context ?? {});
  if (!initialized) return;
  Sentry.captureException(error, {
    tags: { area: "supabase" },
    extra: { label, ...(context ?? {}) },
  });
}

/** Record that a content pool was too small to fill a session without
 * repetition (#27). Fired from the item pipelines (pullItems /
 * useStoryCorpora) ONLY when at least one duplicate was served, so a
 * well-resourced skill is silent. A breadcrumb (not an exception): it
 * has no user-facing impact — the bounded-repetition cap already handled
 * the shortfall — but it tells us WHICH skills are starving so Phase H
 * content authoring can be prioritized. No-op when init didn't run
 * (dev / no DSN). Safe to call unconditionally. */
export function addContentPoolBreadcrumb(info: {
  moduleId: string;
  requestedCount: number;
  uniqueItemsServed: number;
  duplicatesServed: number;
}): void {
  if (!initialized) return;
  Sentry.addBreadcrumb({
    category: "content-pool",
    level: "info",
    message: `content pool short for ${info.moduleId}: served ${info.uniqueItemsServed} unique + ${info.duplicatesServed} repeat`,
    data: { ...info },
  });
}

/** MA-306 — record that the adaptation engine's walkback substitution
 * (resolveServedSkillId, useEngineQuestions.ts) targeted a skill with an
 * EMPTY servable pool under the audience actually being queried, and the
 * substitution was refused (served requestedSkillId instead). Without this
 * refusal the kid would have landed on SessionLoadFallback's dead end
 * ("No questions ready here yet") — a breadcrumb, not an exception, since
 * the kid's session still opens; it tells us WHICH walkback targets are
 * thin so the walkback graph (learningProgressions.json) can be corrected
 * or the target's pool authored. No-op when init didn't run (dev / no DSN).
 * Safe to call unconditionally. */
export function addWalkbackFallbackBreadcrumb(info: {
  requestedSkillId: string;
  emptyRemediationSkillId: string;
  servedSkillId: string;
}): void {
  if (!initialized) return;
  Sentry.addBreadcrumb({
    category: "walkback-fallback",
    level: "warning",
    message: `walkback target "${info.emptyRemediationSkillId}" empty for requested skill "${info.requestedSkillId}" — served "${info.servedSkillId}" instead`,
    data: { ...info },
  });
}

/** P-AUTH-GATE-AND-ONBOARDING-DRAFT — record that the auth-gate ladder HELD a
 * provably-returning provisioned parent at the cloud-error retry surface
 * instead of re-onboarding them (QA-AUDIT Area 1 P1). A breadcrumb, not an
 * exception: the underlying fetch failure is already captured by
 * captureSupabaseError("fetchStudents failed", …) in useCloudSync, and the
 * hold itself is a recoverable, user-visible state (the parent can retry). The
 * breadcrumb tells us, in the trail of any later exception, that this session
 * went through the hold path — so a "stuck on retry" report is diagnosable.
 * No-op when init didn't run (dev / no DSN). Safe to call unconditionally.
 *
 * MA-271: this same surface is reused for a SECOND, distinct hold class — an
 * allowlist-check outage for a previously-confirmed parent (see
 * allowlistOutageGuard.js) — rather than the original students-fetch
 * failure. `context.surface` distinguishes the two in the breadcrumb message
 * so a prod trail doesn't conflate them; omitted/unrecognized defaults to the
 * original students-fetch message (no behavior change for the existing
 * call). */
export function captureAuthGateHold(context?: Record<string, unknown> & { surface?: string }): void {
  if (!initialized) return;
  const message = context?.surface === "allowlist"
    ? "held returning parent at cloud-error retry surface (allowlist check failed)"
    : "held returning parent at cloud-error retry surface (fetch failed)";
  Sentry.addBreadcrumb({
    category: "auth-gate",
    level: "warning",
    message,
    data: context ?? {},
  });
}

/** P-AUTH-GATE-AND-ONBOARDING-DRAFT — report a failed onboarding-draft
 * rehydrate (a localStorage read/parse that gets caught so a corrupt or
 * unreadable draft never crashes the onboarding flow — it degrades to an
 * empty draft). Drops a breadcrumb FIRST (so preceding events are on the trail
 * even when init is off and the capture no-ops), then console.error's, then
 * captures when init ran. Distinct `area: onboarding-draft` tag so these are
 * filterable from the supabase paths. Safe to call unconditionally. */
export function captureDraftRehydrateError(
  label: string,
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (initialized) {
    Sentry.addBreadcrumb({
      category: "onboarding-draft",
      level: "error",
      message: label,
      data: context ?? {},
    });
  }
  console.error(`[onboarding-draft] ${label}:`, error, context ?? {});
  if (!initialized) return;
  Sentry.captureException(error, {
    tags: { area: "onboarding-draft" },
    extra: { label, ...(context ?? {}) },
  });
}

/** P-TIER3-INSTRUMENTATION-A — report a partial/failed metadata write
 * (a session-aggregate, milestone, or plan-snapshot row that carries the
 * new parent-dashboard fields). Distinct from captureSupabaseError so the
 * dashboard-foundation write paths are filterable by `area: metadata-write`
 * in Sentry — these fail silently by design (best-effort, fire-and-forget)
 * so a dropped write would otherwise leave no trail. Drops a breadcrumb
 * FIRST (so the preceding events are on the trail even if init is off and
 * the capture no-ops), then console.error's, then captures when init ran.
 * Safe to call unconditionally. */
export function captureMetadataWriteError(
  label: string,
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (initialized) {
    Sentry.addBreadcrumb({
      category: "metadata-write",
      level: "error",
      message: label,
      data: context ?? {},
    });
  }
  console.error(`[metadata-write] ${label}:`, error, context ?? {});
  if (!initialized) return;
  Sentry.captureException(error, {
    tags: { area: "metadata-write" },
    extra: { label, ...(context ?? {}) },
  });
}
